/**
 * The Live round a method card stands for (US-MB-6, W12 fatia 1).
 *
 * ONE rule, TWO mirrors: this validator, called by kit-api's `POST /methods` / `PATCH /methods/:id`,
 * and the seed gate (`infra/seed/validate-methods.py`), which re-implements these bounds in Python
 * and says so — `round-launch.test.ts` runs every authored spec through THIS one, so the two cannot
 * drift in silence. A spec that the library accepted but the planner could not launch — or the
 * reverse — is the failure the rule exists to prevent.
 *
 * It validates SHAPE, never MEANING. `config` is the type's own `QuestionConfig` fields and
 * live-api owns whether they make sense together (an `mc` with one option, a `promptvote` with no
 * vote window): one source of truth for meaning, and its 400 is the message. What is judged here is
 * what live-api cannot judge later — that the card is not carrying a targeting decision (`eligible`,
 * `variants` are a room's business, not a card's), not overriding the identity fields the launcher
 * mints (`id`, `type`, `prompt`, `timeLimitMs`), and not unbounded.
 */

/** The five wire types (constitution #7 — the ceiling, not a starting point). */
export const ROUND_TYPES = ["mc", "wordcloud", "open", "promptvote", "picker"] as const;
export type RoundType = (typeof ROUND_TYPES)[number];

/**
 * Prompt ceiling, in **UTF-16 code units** — what `String.length` counts, so "≤ 280" means the
 * same number here and in the Python gate (which measures `utf-16-le` bytes / 2 for exactly that).
 * A card's own bound: live-api's `QUESTION_MAX` (live/text.ts) bounds PARTICIPANT text, and
 * `validateQuestion` puts no length limit on a host's prompt at all. Absent = the method title.
 */
export const PROMPT_MAX = 280;
/** live-api's `MAX_ROUND_MS` is the ceiling; the floor keeps a card from opening a round that
 *  closes before a phone has finished loading it. Absent = untimed, and the host closes. */
export const ROUND_MS_MIN = 5_000;
export const ROUND_MS_MAX = 600_000;
/**
 * Keys a card may never carry.
 *
 * The first four are minted by whoever opens the round (`id` carries the block, `prompt` falls back
 * to the title, `type`/`timeLimitMs` are the spec's own fields) — inside `config` they would be a
 * second, silent copy that wins or loses depending on spread order. The last two are targeting:
 * who may answer and who sees what is decided in the room, in front of the people it applies to,
 * never baked into library content.
 */
export const FORBIDDEN_CONFIG_KEYS = ["id", "type", "prompt", "timeLimitMs", "eligible", "variants"] as const;
/** A card's defaults, not a payload. Bounded because `round_spec` is TEXT with no CHECK. */
export const CONFIG_MAX_BYTES = 4_096;

export interface RoundSpec {
  type: RoundType;
  /** Absent = the method title. */
  prompt?: string;
  /** Absent = untimed. */
  timeLimitMs?: number;
  /** The type's own `QuestionConfig` fields — options, budget, vote window, upvotes. */
  config?: Record<string, unknown>;
}

export type RoundSpecCheck =
  | { ok: true; spec: RoundSpec }
  /** `reason` names the rule that fired. Not on the wire (#6 — codes only): it is what makes a
   *  test fail for the reason it meant to, and what a seed author reads in the gate's output. */
  | { ok: false; code: "INVALID_ROUND_SPEC"; reason: string };

/**
 * Validate a spec from a request body or a stored TEXT column. Accepts the JSON string as well as
 * the parsed value, so a malformed column cannot throw at whichever caller forgot the try/catch.
 */
export function validateRoundSpec(value: unknown): RoundSpecCheck {
  const raw = typeof value === "string" ? parseJson(value) : value;
  if (!isPlainObject(raw)) return bad("not an object");

  const { type, prompt, timeLimitMs, config } = raw;
  if (typeof type !== "string" || !(ROUND_TYPES as readonly string[]).includes(type)) return bad("type");
  const spec: RoundSpec = { type: type as RoundType };

  if (prompt !== undefined) {
    if (typeof prompt !== "string" || prompt.trim() === "" || prompt.length > PROMPT_MAX) return bad("prompt");
    spec.prompt = prompt;
  }
  if (timeLimitMs !== undefined) {
    if (!isRoundWindow(timeLimitMs)) return bad("timeLimitMs");
    spec.timeLimitMs = timeLimitMs;
  }
  if (config !== undefined) {
    if (!isPlainObject(config)) return bad("config");
    for (const key of FORBIDDEN_CONFIG_KEYS) if (key in config) return bad(`config.${key}`);
    // `voteTimeLimitMs` is the ONE other deadline a card can carry (a `promptvote`'s vote phase),
    // and it escaped every bound: live-api only requires it to be > 0, so a card could ship a
    // five-day vote phase into a twelve-hour room and the round would outlive the session. Same
    // window as the round's own timer, which is the only window a live round has (round-1 review).
    if (config.voteTimeLimitMs !== undefined && !isRoundWindow(config.voteTimeLimitMs)) return bad("config.voteTimeLimitMs");
    // A single-screen round (US-LV-22). Live-api owns the full rule; what a card can get wrong on its
    // own is the shape live-api would refuse at the planner's click: not `true`, not an `mc`, or a
    // clock on a round the host closes with the room's choice.
    if (config.hosted !== undefined) {
      if (config.hosted !== true) return bad("config.hosted");
      if (spec.type !== "mc") return bad("config.hosted needs type mc");
      if (spec.timeLimitMs !== undefined) return bad("config.hosted is untimed");
    }
    const json = safeStringify(config);
    if (json === null) return bad("config not serialisable");
    if (utf8Bytes(json) > CONFIG_MAX_BYTES) return bad("config too large");
    spec.config = config;
  }
  return { ok: true, spec };
}

/**
 * The NORMALISED spec, or null for absent-or-malformed. Returns the validator's own object, never
 * the caller's: a request body may carry unbounded extra top-level keys, and persisting what
 * arrived would store every one of them.
 */
export function parseRoundSpec(value: unknown): RoundSpec | null {
  if (value === undefined || value === null) return null;
  const checked = validateRoundSpec(value);
  return checked.ok ? checked.spec : null;
}

/**
 * The round a launcher actually opens: the card's own `config`, plus the four fields the launcher
 * mints. As far as Kit is allowed to know it — Kit deliberately does not model live-api's wire
 * types, so the caller casts this to its own hand-synced `QuestionConfig`.
 */
export type LaunchedRound = Record<string, unknown> & { id: string; type: RoundType; prompt: string };

/**
 * Assemble the round for `POST /sessions/:id/questions` (US-MB-6).
 *
 * The forbidden keys are DELETED from `config`, not merely overwritten by the fields below. Spread
 * order alone was not enough, and the test says why: a stored `config.timeLimitMs` survived on a
 * spec that carries no timer of its own, because "untimed" is spelled by OMITTING the key — so the
 * launcher had nothing to overwrite it with, and a card could re-point the deadline of a round it
 * does not own. `validateRoundSpec` refuses these keys on the way in; this is what makes that
 * refusal not load-bearing for a row written before the rule existed.
 *
 * `eligible`/`variants` therefore cannot be sent at all — targeting is decided in the room, in
 * front of the people it applies to, never carried by library content.
 *
 * `prompt` falls back to the method's title: a card whose round asks its own question says so, and
 * one that does not still opens with something the room can read.
 */
export function roundConfigFor(spec: RoundSpec, methodTitle: string, qid: string): LaunchedRound {
  const config: Record<string, unknown> = { ...(spec.config ?? {}) };
  for (const key of FORBIDDEN_CONFIG_KEYS) delete config[key];
  return {
    ...config,
    id: qid,
    type: spec.type,
    prompt: spec.prompt ?? methodTitle,
    // Absent, never 0: a `timeLimitMs: 0` would arm a deadline at the instant the round opened.
    ...(spec.timeLimitMs === undefined ? {} : { timeLimitMs: spec.timeLimitMs }),
  };
}

/** An integer inside the window a live round can actually have. Shared by both deadlines. */
function isRoundWindow(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= ROUND_MS_MIN && (value as number) <= ROUND_MS_MAX;
}

function bad(reason: string): RoundSpecCheck {
  return { ok: false, code: "INVALID_ROUND_SPEC", reason };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function safeStringify(value: unknown): string | null {
  try {
    return JSON.stringify(value) ?? null;
  } catch {
    return null; // a cycle, or a BigInt
  }
}

/** UTF-8 length without `TextEncoder`: kit-core compiles with `types: []`, so neither the DOM nor
 *  node globals are in scope. Bytes, not code units, so the Python gate can count the same number. */
function utf8Bytes(text: string): number {
  let bytes = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 0;
    bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
  }
  return bytes;
}
